import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, Loader2, Receipt, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  campaign_reactivation: "Campaign fee",
  wallet_topup: "Wallet top-up",
};
const KIND_STYLES = {
  campaign_reactivation: "text-amber-500 border-amber-500/40 bg-amber-500/5",
  wallet_topup: "text-emerald-500 border-emerald-500/40 bg-emerald-500/5",
};

const fmtINR = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await api.adminListInvoices({ limit: 500 });
      setInvoices(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load invoices.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.trim().toLowerCase();
    return invoices.filter((inv) =>
      [
        inv.invoice_number,
        inv.vendor_business,
        inv.vendor_email,
        inv.campaign_name,
        inv.razorpay_payment_id,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [invoices, search]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, i) => s + Number(i.total_amount || 0), 0);
    const campaign = rows
      .filter((i) => i.kind === "campaign_reactivation")
      .reduce((s, i) => s + Number(i.total_amount || 0), 0);
    const wallet = rows
      .filter((i) => i.kind === "wallet_topup")
      .reduce((s, i) => s + Number(i.total_amount || 0), 0);
    return { total, campaign, wallet, count: rows.length };
  }, [rows]);

  const handleDownload = async (inv) => {
    setDownloadingId(inv.invoice_number);
    try {
      const r = await api.adminDownloadInvoicePdf(inv.invoice_number);
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (err) {
      const msg =
        err?.response?.status === 404
          ? "This invoice's PDF isn't available. It may need regeneration."
          : "Couldn't download the PDF. Please try again.";
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Invoices</h1>
          <p className="text-muted-foreground text-sm">
            Every GST invoice issued across all vendors — campaign fees + wallet
            top-ups. PDFs stream on demand from object storage.
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => load({ refresh: true })}
          disabled={refreshing || loading}
          data-testid="admin-invoices-refresh"
          aria-label="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5"><Skeleton className="h-16 w-full" /></Card>
          ))
        ) : (
          <>
            <Card className="p-5" data-testid="admin-invoices-total">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Total invoiced
              </p>
              <p className="font-heading text-3xl font-semibold mt-1 tabular-nums">
                {fmtINR(summary.total)}
              </p>
            </Card>
            <Card className="p-5" data-testid="admin-invoices-campaign">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Campaign fees
              </p>
              <p className="font-heading text-3xl font-semibold mt-1 tabular-nums">
                {fmtINR(summary.campaign)}
              </p>
            </Card>
            <Card className="p-5" data-testid="admin-invoices-wallet">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Wallet top-ups
              </p>
              <p className="font-heading text-3xl font-semibold mt-1 tabular-nums">
                {fmtINR(summary.wallet)}
              </p>
            </Card>
            <Card className="p-5" data-testid="admin-invoices-count">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Invoices shown
              </p>
              <p className="font-heading text-3xl font-semibold mt-1 tabular-nums">
                {summary.count.toLocaleString("en-IN")}
              </p>
            </Card>
          </>
        )}
      </div>

      <Card className="p-0 overflow-hidden" data-testid="admin-invoices-ledger">
        <div className="p-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Invoice ledger
            </h2>
            <p className="text-xs text-muted-foreground">
              Source: <code>GET /api/admin/invoices</code>
            </p>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by vendor, invoice #, campaign…"
              className="pl-9 w-72"
              data-testid="admin-invoices-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="admin-invoices-empty">
            <p className="text-sm text-muted-foreground">
              {search.trim()
                ? "No invoices match your filter."
                : "No invoices have been issued yet. They will appear here as vendors reactivate campaigns or top up their wallets."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((inv, idx) => {
                  const isDownloading = downloadingId === inv.invoice_number;
                  return (
                    <TableRow
                      key={inv.invoice_number || idx}
                      data-testid={`admin-invoices-row-${inv.invoice_number || idx}`}
                    >
                      <TableCell className="font-mono text-xs">
                        {inv.invoice_number}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {inv.vendor_business || "—"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {inv.vendor_email || inv.vendor_id || ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            KIND_STYLES[inv.kind] ||
                            "text-muted-foreground border-border"
                          }
                        >
                          {KIND_LABEL[inv.kind] || inv.kind || "unknown"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {inv.campaign_name || (inv.kind === "wallet_topup" ? "—" : "")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {fmtINR(inv.total_amount)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {fmtDate(inv.issued_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isDownloading || !inv.pdf_path}
                          onClick={() => handleDownload(inv)}
                          data-testid={`admin-invoice-download-${inv.invoice_number}`}
                        >
                          {isDownloading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-1.5">PDF</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
